/**
 * @file ShopOwnerFields.tsx
 * @description Поле «Владелец» формы «Новый магазин» (phase19.28 этап А;
 * uiux-platform-shops §3; PL2, PL7). Контракт `value / onChange` — общий с
 * `ShopCreateMainFields`.
 *
 * Одна почта: нашёлся пользователь — «Найден: Анна Смирнова», ему дадут
 * роль «Владелец магазина»; не нашёлся — «отправим приглашение» (ссылка на
 * 7 дней) и поле «Имя владельца» для письма. У пользователя роль
 * платформы — владельцем он быть не может (PL2), об этом сразу под полем.
 */

import type { OwnerLookup } from "../../../lib/api-platform-shops";
import { OWNER_CONFLICT_TEXT, isEmail, type ShopCreateErrors, type ShopCreateValue } from "../../../lib/platform/shop-create-form";
import { ShopTextField } from "./ShopTextField";

interface Props {
  value: ShopCreateValue;
  onChange: (next: ShopCreateValue) => void;
  errors: ShopCreateErrors;
  /** Ответ owner-lookup для текущей почты; null — ещё не проверили. */
  lookup: OwnerLookup | null;
  lookupUnavailable: boolean;
  disabled?: boolean;
}

function ownerHint(value: ShopCreateValue, lookup: OwnerLookup | null, lookupUnavailable: boolean): string {
  if (!isEmail(value.ownerEmail)) return "Почта человека, который будет вести магазин";
  if (lookup?.conflict) return OWNER_CONFLICT_TEXT[lookup.conflict];
  if (lookup?.user) return `Найден: ${lookup.user.displayName || lookup.user.email} — получит роль «Владелец магазина»`;
  if (lookup) return "Не найден — отправим приглашение на 7 дней";
  if (lookupUnavailable) return "Поиск пользователей недоступен — если такого нет, отправим приглашение";
  return "Ищем пользователя…";
}

export function ShopOwnerFields({ value, onChange, errors, lookup, lookupUnavailable, disabled }: Props) {
  const invite = !lookup?.user;
  return (
    <div className="flex flex-col gap-4">
      <ShopTextField
        id="shop-owner-email"
        label="Владелец (почта)"
        required
        type="email"
        value={value.ownerEmail}
        onChange={(ownerEmail) => onChange({ ...value, ownerEmail })}
        error={errors.owner}
        hint={ownerHint(value, lookup, lookupUnavailable)}
        autoComplete="off"
        disabled={disabled}
      />
      {invite ? (
        <ShopTextField
          id="shop-owner-name"
          label="Имя владельца"
          required
          value={value.ownerName}
          onChange={(ownerName) => onChange({ ...value, ownerName })}
          error={errors.ownerName}
          hint="Только для приглашения: так к нему обратится письмо"
          maxLength={120}
          autoComplete="off"
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
